import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { error } from "console";

export type RoleEntry = {
  name: string;
  qualification: string;
  designation: string;
};

export type InvoiceFooter = {
  mob1: string;
  mob2: string;
  address: string;
};

export type FooterData = {
  Pathologist: RoleEntry;
  Microbiologist: RoleEntry;
  Biochemist: RoleEntry;
  Technologist: RoleEntry;
  invoiceFooter: InvoiceFooter;
  verificationText: string;
};

const ROLES = ["Pathologist", "Microbiologist", "Biochemist", "Technologist"] as const;
const DEFAULT_VERIFICATION_TEXT =
  "Electronically verified. No signature required";

const emptyFooterData = (): FooterData => ({
  Pathologist: { name: "", qualification: "", designation: "" },
  Microbiologist: { name: "", qualification: "", designation: "" },
  Biochemist: { name: "", qualification: "", designation: "" },
  Technologist: { name: "", qualification: "", designation: "" },
  invoiceFooter: { mob1: "", mob2: "", address: "" },
  verificationText: DEFAULT_VERIFICATION_TEXT,
});

const LabPageFooter: React.FC = () => {
  const [footerData, setFooterData] = useState<FooterData>(emptyFooterData);
  const [loading, setLoading] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const API_ROOT = process.env.REACT_APP_LAB_URL;

  // fetch saved footer data (returns object with 4 roles)
  const fetchFooterData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.get(`${API_ROOT}/page-footer/getPageFooter`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (resp?.data?.isSuccess && resp.data.data) {
        const data = resp.data.data;
        setFooterData({
          Pathologist: data.Pathologist || { name: "", qualification: "", designation: "" },
          Microbiologist: data.Microbiologist || { name: "", qualification: "", designation: "" },
          Biochemist: data.Biochemist || { name: "", qualification: "", designation: "" },
          Technologist: data.Technologist || { name: "", qualification: "", designation: "" },
          invoiceFooter: data.invoiceFooter || { mob1: "", mob2: "", address: "" },
          verificationText:
            typeof data.verificationText === "string"
              ? data.verificationText
              : DEFAULT_VERIFICATION_TEXT,
        });
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error fetching footer data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  const updateRole = (
    role: (typeof ROLES)[number],
    field: keyof RoleEntry,
    value: string
  ) => {
    setFooterData((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = footerData;

      await axios.put(`${API_ROOT}/page-footer/setPageFooter`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Saved successfully");
      await fetchFooterData();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error saving footer");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_ROOT}/page-footer/removePageFooter`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFooterData(emptyFooterData());
      toast.success("Deleted successfully");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Error deleting footer");
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <ToastContainer />
      <Grid container spacing={3}>
        {/* Form */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, maxHeight: "70vh", overflow: "auto" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Page Footer Settings
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Verification Text
            </Typography>
            <TextField
              label="Verification Text"
              value={footerData.verificationText}
              onChange={(e) =>
                setFooterData((prev) => ({
                  ...prev,
                  verificationText: e.target.value,
                }))
              }
              placeholder={DEFAULT_VERIFICATION_TEXT}
              fullWidth
              size="small"
              className="custom-input"
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <Divider sx={{ mb: 2 }} />

            {ROLES.map((role) => (
              <Box key={role} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {role}
                </Typography>

                <TextField
                  label="Name"
                  value={footerData[role].name}
                  onChange={(e) => updateRole(role, "name", e.target.value)}
                  fullWidth
                  size="small"
                  className="custom-input"
                  sx={{ mb: 1.5 }}
                />

                <TextField
                  label="Qualification"
                  value={footerData[role].qualification}
                  onChange={(e) => updateRole(role, "qualification", e.target.value)}
                  fullWidth
                  size="small"
                  className="custom-input"
                  sx={{ mb: 1.5 }}
                />

                <TextField
                  label="Designation"
                  value={footerData[role].designation}
                  onChange={(e) => updateRole(role, "designation", e.target.value)}
                  fullWidth
                  size="small"
                  className="custom-input"
                />

                {role !== "Technologist" && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Invoice Footer
            </Typography>

            <TextField
              label="Mobile 1"
              value={footerData.invoiceFooter.mob1}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 11) {
                  setFooterData((prev) => ({
                    ...prev,
                    invoiceFooter: { ...prev.invoiceFooter, mob1: value }
                  }));
                }
              }}
              placeholder="03001234567"
              fullWidth
              size="small"
              className="custom-input"
              sx={{ mb: 1.5 }}
              inputProps={{ maxLength: 11 }}
            />

            <TextField
              label="Mobile 2"
              value={footerData.invoiceFooter.mob2}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 11) {
                  setFooterData((prev) => ({
                    ...prev,
                    invoiceFooter: { ...prev.invoiceFooter, mob2: value }
                  }));
                }
              }}
              placeholder="03001234567"
              fullWidth
              size="small"
              className="custom-input"
              sx={{ mb: 1.5 }}
              inputProps={{ maxLength: 11 }}
            />

            <TextField
              label="Address"
              value={footerData.invoiceFooter.address}
              onChange={(e) => setFooterData((prev) => ({
                ...prev,
                invoiceFooter: { ...prev.invoiceFooter, address: e.target.value }
              }))}
              fullWidth
              size="small"
              className="custom-input"
              multiline
              rows={2}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
              <Button variant="outlined" color="error" onClick={handleDelete} disabled={loading}>
                Delete All
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} /> : null}
              >
                Save
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{ p: 3, position: "relative", overflow: "hidden", height: "66vh" }}
          >
            <Typography
              component="div"
              sx={{
                position: "absolute",
                inset: 0,
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                opacity: 0.06,
                fontWeight: 800,
                fontSize: 72,
                transform: "rotate(-30deg)",
                letterSpacing: 6,
                zIndex: 0,
              }}
            >
              Footer Preview
            </Typography>

            <Box sx={{ position: "relative", zIndex: 1, height: "100%" }} ref={previewRef}>
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 16,
                  px: 2,
                }}
              >
                {/* Electronically verified text */}
                {footerData.verificationText?.trim() ? (
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: "center",
                      display: "block",
                      color: "black",
                      fontSize: "1rem",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {footerData.verificationText.trim()}
                  </Typography>
                ) : null}

                {/* Top line */}
                <Box sx={{ borderTop: "3px solid #000", mb: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "space-around",
                    flexWrap: "wrap",
                  }}
                >
                {ROLES.filter((role) => {
                  const data = footerData[role];
                  return data.name?.trim() || data.qualification?.trim() || data.designation?.trim();
                }).map((role, idx) => {
                  const data = footerData[role];

                  return (
                    <Box
                      key={role}
                      sx={{
                        flex: "0 0 auto",
                        minWidth: 120,
                        textAlign: "left",
                        pl: idx > 0 ? 2 : 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                      >
                        {data.name?.trim() || " "}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {data.qualification?.trim() || " "}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {data.designation?.trim() || " "}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Bottom line */}
              <Box sx={{ borderBottom: "3px solid #000", mt: 1 }} />

              {/* Printing time and generated by */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "column",
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" color="black" sx={{ fontSize: "0.75rem" }}>
                 <span className="font-bold "> Printing Time:</span> {new Date().toLocaleString()}
                </Typography>
                <Typography variant="caption" color="black" sx={{ fontSize: "0.75rem" }}>
                  <span className="font-bold ">Generated By:</span> System
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  </Box>
);
};

export default LabPageFooter;
